import React from 'react';
import startCase from 'lodash/startCase';
import countBy from 'lodash/countBy';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import endsWith from 'lodash/endsWith';
import includes from 'lodash/includes';
import { Link } from 'react-router-dom';

import messages from 'Components/FacetSearch/LanguagesStrings';

function getMainType(hits) {
  const counts = countBy(hits.map(h => h.display_type));
  let maxCount = 0;
  let maxType = 'game';

  forEach(counts, (count, type) => {
    if (count > maxCount) {
      maxType = type;
      maxCount = count;
    }
  });

  switch (maxType) {
    case 'game':
    case 'bundle':
    case 'dlc':
      return 'games';
    case 'book':
    case 'book-bundle':
      return 'books';
    case 'comic':
    case 'comic-bundle':
      return 'comics';
    case 'elearning':
    case 'elearning-bundle':
      return 'elearning';
    case 'software':
    case 'software-bundle':
      return 'software';
    case 'audio':
    case 'audio-bundle':
      return 'audio';
    case 'video':
    case 'video-bundle':
      return 'video';
    case 'graphic-novel':
      return 'graphicnovel';
    default:
      return 'games';
  }
}

function getMainDrm(hits) {
  const counts = countBy(hits.map(h => get(h, 'drm[0]')));
  let maxCount = 0;
  let maxDrm;

  forEach(counts, (count, type) => {
    if (count > maxCount) {
      maxDrm = type;
      maxCount = count;
    }
  });

  return maxDrm;
}

const typeCheckExclusionList = [
  'NonGaming Bundles',
  'Skill Up Sale',
  'Dollar Collections',
  'Programmers Day',
  'Dollar Deals',
  'Cyberpunk Collection',
  'Dark Horse Comics',
  'One Dollar Wonders Collection',
];

export function facetSeoTitle(formatMessage, name, hits, page) {
  const pageLabel = formatMessage(messages.page);
  const subtitleString = formatMessage(messages.subtitle);

  const mainType = getMainType(hits);
  const mainDrm = getMainDrm(hits);

  let subtitle = '';

  if (mainType === 'games' && mainDrm === 'steam') {
    subtitle = subtitleString;
  }

  const typeMessage = messages[mainType];

  const typeCheck = (endsWith(name.toLowerCase(), mainType) || (window.locale && window.locale !== 'en') || includes(typeCheckExclusionList, name)) ? '' : formatMessage(typeMessage);

  return `${name} ${typeCheck} ${subtitle ? `| ${subtitle}` : ''} ${page > 1 ? `| ${pageLabel} ${page} ` : ''}| Fanatical`;
}

export function facetH1Title(formatMessage, name, hits, page, facet, saleDisplayName) {
  const pageLabel = formatMessage(messages.page);

  const mainType = getMainType(hits);

  const typeMessage = messages[mainType];

  const typeCheck = (facet === 'collections' || endsWith(name.toLowerCase(), mainType) || (window.locale && window.locale !== 'en') || includes(typeCheckExclusionList, name) || saleDisplayName) ? '' : formatMessage(typeMessage);

  return `${name} ${typeCheck} ${page > 1 ? `| ${pageLabel} ${page}` : ''}`;
}

export function facetTitle(formatMessage, name, hits) {
  const mainType = getMainType(hits);

  const typeMessage = messages[mainType];

  const typeCheck = (endsWith(name.toLowerCase(), mainType) || (window.locale && window.locale !== 'en') || includes(typeCheckExclusionList, name)) ? '' : formatMessage(typeMessage);

  return `${name} ${typeCheck}`;
}

export function facetDesc(formatMessage, name, facetData, facet, relatedFacetData, hits, noLinks) {
  if (facetData && facetData.desc) {
    return facetData.desc;
  }

  const mainType = getMainType(hits);

  const typeMessage = messages[mainType];

  let autoDesc1 = '';
  let autoDesc2 = '';

  if (!noLinks && relatedFacetData.categories &&
    !!relatedFacetData.categories.length) {
    if (facet === 'franchises') {
      autoDesc1 = formatMessage(messages.franchiseAutoCategories, {
        categories: relatedFacetData.categories.slice(0, 3).map(genre => <Link key={genre.slug} to={`/categories/${genre.slug}`}>{startCase(genre.name)}</Link>)
          .reduce((prev, curr, index, arr) => [prev, index === arr.length - 1 ? ` ${formatMessage(messages.and)} ` : ', ', curr]),
        type: formatMessage(typeMessage),
        name: name.replace(/^The /, ''),
      });
    } else {
      autoDesc1 = formatMessage(messages.autoCategories, {
        categories: relatedFacetData.categories.slice(0, 3).map(genre => <Link key={genre.slug} to={`/categories/${genre.slug}`}>{startCase(genre.name)}</Link>)
          .reduce((prev, curr, index, arr) => [prev, index === arr.length - 1 ? ` ${formatMessage(messages.and)} ` : ', ', curr]),
        type: formatMessage(typeMessage),
        name,
      });
    }
  }

  if (!noLinks && relatedFacetData.franchises &&
    !!relatedFacetData.franchises.length) {
    autoDesc2 = formatMessage(messages.autoFranchises, {
      count: relatedFacetData.franchises.length,
      franchises: relatedFacetData.franchises.slice(0, 4).map(franchise => <Link key={franchise.slug} to={`/franchises/${franchise.slug}`}>{startCase(franchise.name)}</Link>)
        .reduce((prev, curr, index, arr) => [prev, index === arr.length - 1 ? ` ${formatMessage(messages.and)} ` : ', ', curr]),
      type: formatMessage(typeMessage),
    });
  }

  if (!noLinks && (autoDesc1 || autoDesc2)) {
    return <React.Fragment>{autoDesc1}{autoDesc1 && autoDesc2 ? ' ' : ''}{autoDesc2}</React.Fragment>;
  }

  return `${messages[`${facet}GameFacetDesc`] && mainType === 'games' ? formatMessage(messages[`${facet}GameFacetDesc`], { [facet.replace('-', '')]: name }) : formatMessage(messages[`${facet}FacetDesc`], { [facet.replace('-', '')]: name, type: mainType === 'games' ? formatMessage(messages.pcgames) : formatMessage(typeMessage) })}`;
}
